import { useState, ReactNode } from 'react';
import { Stack } from '@mui/material';
import { Typography, IconButton } from '@sweep-io/sweep-design';
import { Wrench, Edit, Close, PlusFile, Tuning } from '@sweep-io/sweep-design/dist/icons';
import { useSelector } from 'react-redux';
import AgentInstructions from './AgentInstructions';
import AgentFiles from './AgentFiles';
import useAiAgents from '../useAiAgents';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { AI_AGENTS_INFO, BaseAgentProps, INSTRUCTIONS_SUBTITLE } from '../aiAgentsConsts';
import { AI_AGENTS_EVENTS } from '../../../services/events';
import useAgentsChats from '../useAgentsChats';
import BestPracticesContent from '../../bestPractices/BestPracticesContent';
import useConfirm from '../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { ConfirmLeaveWithoutSaveDialog } from '../../common/dialogs/ConfirmLeaveWithoutSave/ConfirmLeaveWithoutSaveDialog';
import { AgentSettingsDialogLayout } from './AgentSettingsDialogLayout';
import { SectionMenuItem } from './SectionMenuItem';
import { selectUserInfoData } from '../../../reducers/userInfoReducer';

export enum SettingSection {
  Instructions = 'Instructions',
  Files = 'Files',
  BestPractices = 'BestPractices',
  Tuning = 'Tuning',
}

const SECTIONS_INFO: {
  [kei in SettingSection]: {
    name: string;
    icon: ReactNode;
    getIsEnabled: (agentInfo: BaseAgentProps, userEmail?: string) => boolean;
  };
} = {
  [SettingSection.Instructions]: {
    name: 'Instructions',
    icon: <Wrench size="medium" />,
    getIsEnabled: () => true,
  },
  [SettingSection.Files]: {
    name: 'Files',
    icon: <PlusFile size="medium" />,
    getIsEnabled: () => true,
  },
  [SettingSection.BestPractices]: {
    name: 'Best Practices',
    icon: <Edit size="medium" />,
    getIsEnabled: (agentInfo) => !!agentInfo.isSupportBestPractices,
  },
  [SettingSection.Tuning]: {
    name: 'Tuning',
    icon: <Tuning size="medium" />,
    getIsEnabled: (agentInfo, userEmail) => !!userEmail?.endsWith('@sweep.io'),
  },
};

const sections = Object.values(SettingSection);

const AgentSettingsDialog = ({
  isOpened,
  closeDialog,
  initialSection,
}: {
  isOpened: boolean;
  closeDialog: () => void;
  initialSection?: SettingSection;
}) => {
  const [selectedSection, setSelectedSection] = useState(
    initialSection || SettingSection.Instructions,
  );
  const [isBestPracticesPristine, setIsBestPracticesPristine] = useState(true);
  const [isInstructionsPristine, setIsInstructionsPristine] = useState(true);
  const [isTuningPristine, setIsTuningPristine] = useState(true);
  const { activeAgent } = useAgentsChats();
  const agentInfo = activeAgent ? AI_AGENTS_INFO[activeAgent?.type] : undefined;
  const { editAgentUserInstructions, editAgentSystemInstructions, addAgentFiles, deleteFile } =
    useAiAgents();
  const sendBiEvent = useSendBiEvent();
  const { openConfirm } = useConfirm();
  const userInfo = useSelector(selectUserInfoData);

  const onUserInstructionsChange = async (instructions: string) => {
    if (activeAgent) {
      sendBiEvent({ name: AI_AGENTS_EVENTS.editInstructions });
      await editAgentUserInstructions(activeAgent.id, instructions);
    }
  };

  const onSystemInstructionsChange = async (systemInstructions: string) => {
    if (activeAgent) {
      await editAgentSystemInstructions(activeAgent.id, systemInstructions);
    }
  };

  const onFilesAdd = async (files: File[]) => {
    if (activeAgent) {
      sendBiEvent({ name: AI_AGENTS_EVENTS.addFiles });
      await addAgentFiles(activeAgent.id, files);
    }
  };

  const onFileRemove = async (fileId: string) => {
    if (activeAgent) {
      await deleteFile(activeAgent.id, fileId);
    }
  };

  const hasUnsavedChanges = () => {
    return (
      (selectedSection === SettingSection.BestPractices && !isBestPracticesPristine) ||
      (selectedSection === SettingSection.Instructions && !isInstructionsPristine) ||
      (selectedSection === SettingSection.Tuning && !isTuningPristine)
    );
  };

  const handleSectionChange = async (newSection: SettingSection) => {
    if (hasUnsavedChanges()) {
      const isConfirmed = await openConfirm(<ConfirmLeaveWithoutSaveDialog />);
      if (!isConfirmed) return;
    }
    setSelectedSection(newSection);
  };

  const handleTryCloseDialog = async () => {
    if (hasUnsavedChanges()) {
      const isConfirmed = await openConfirm(<ConfirmLeaveWithoutSaveDialog />);
      if (!isConfirmed) return;
    }
    closeDialog();
  };

  if (!activeAgent || !agentInfo) {
    return null;
  }

  return (
    <AgentSettingsDialogLayout
      isOpen={isOpened}
      onClose={handleTryCloseDialog}
      menuJsx={
        <>
          {sections.map((section) => {
            const { getIsEnabled, name, icon } = SECTIONS_INFO[section];
            if (!getIsEnabled(agentInfo, userInfo?.email)) {
              return null;
            }
            return (
              <SectionMenuItem
                key={section}
                isSelected={selectedSection === section}
                isDisabled={false}
                onClick={() => handleSectionChange(section)}
              >
                <Stack direction="row" gap={1} alignItems="center">
                  {icon}
                  <Typography variant="body"> {name}</Typography>
                </Stack>
              </SectionMenuItem>
            );
          })}
        </>
      }
    >
      <>
        {selectedSection === SettingSection.Instructions && (
          <AgentInstructions
            closeDialog={handleTryCloseDialog}
            value={activeAgent.userInstructions ?? ''}
            onChange={onUserInstructionsChange}
            placeholder={agentInfo.instructionsPlaceholder ?? ''}
            onPristineStateChange={setIsInstructionsPristine}
            title="Instructions"
            subtitle={INSTRUCTIONS_SUBTITLE}
            permission="edit:ai-agent"
          />
        )}

        {selectedSection === SettingSection.Files && (
          <AgentFiles
            addFiles={onFilesAdd}
            removeFile={onFileRemove}
            files={activeAgent.files ?? []}
            agentName={activeAgent.name ?? ''}
            closeDialog={handleTryCloseDialog}
          />
        )}

        {selectedSection === SettingSection.BestPractices && (
          <Stack overflow="auto" flex={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" p={2}>
              <Typography variant="h3">Best Practices</Typography>
              <IconButton onClick={handleTryCloseDialog} variant="flat" size="small">
                <Close />
              </IconButton>
            </Stack>
            <BestPracticesContent
              agentType={activeAgent.type}
              agent={activeAgent}
              onPristineStateChange={setIsBestPracticesPristine}
            />
          </Stack>
        )}

        {selectedSection === SettingSection.Tuning && (
          <AgentInstructions
            closeDialog={handleTryCloseDialog}
            value={activeAgent.systemInstructions ?? ''}
            onChange={onSystemInstructionsChange}
            placeholder="e.g., Focus on providing concise technical answers. Always prioritize security best practices in recommendations."
            onPristineStateChange={setIsTuningPristine}
            title="Tuning"
            subtitle="The following instructions are sent to the agent as additional context, but are only visible to Sweep users"
            permission="edit:ai-agent:system-instructions"
          />
        )}
      </>
    </AgentSettingsDialogLayout>
  );
};

export default AgentSettingsDialog;
